import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The accent a tile is keyed to.
 *
 * Every one of these resolves to a ramp the brand already owns — the gold and
 * navy from the logo, the azure sampled from its mark, and two neighbours of
 * that azure. Deliberately not a rainbow: a dashboard where each card invents
 * a colour stops looking like one product.
 */
export type TileTone = 'gold' | 'azure' | 'teal' | 'indigo' | 'navy';

interface TileProps {
  label: string;
  value: string;
  /** e.g. "12.4%". Omit when there is nothing to compare against. */
  delta?: string;
  up?: boolean;
  note?: string;
  tone?: TileTone;
  /** A short glyph for the chip. Decorative — the label carries the meaning. */
  icon?: ReactNode;
  /**
   * 0–100. Draws a rail under the value.
   *
   * Only pass this where the number really is a proportion; a bar under a
   * count implies a ceiling that does not exist.
   */
  progress?: number;
  /** Where the tile leads. Given, the whole tile is a link to it. */
  to?: string;
}

/** The KPI tile. A delta is only ever shown when a real comparison exists. */
export function Tile({
  label,
  value,
  delta,
  up = true,
  note,
  tone = 'navy',
  icon,
  progress,
  to,
}: TileProps) {
  const body = (
    <>
      {icon ? (
        <span className="tile__chip" aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <p className="tile__label">{label}</p>
      <p className="tile__value">{value}</p>

      {typeof progress === 'number' ? (
        <span className="tile__rail" role="img" aria-label={`${Math.round(progress)} percent`}>
          <span
            className="tile__rail-fill"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </span>
      ) : null}

      {delta || note ? (
        <p className="tile__note">
          {delta ? (
            <span className={`tile__delta tile__delta--${up ? 'up' : 'down'}`}>
              {up ? '▲' : '▼'} {delta}
            </span>
          ) : null}{' '}
          {note}
        </p>
      ) : null}
    </>
  );

  // A figure on a dashboard reads as a way in to what it counts, so a tile
  // that has somewhere to go is a link — the whole card, not a hidden word.
  return to ? (
    <Link className={`tile tile--${tone} tile--link`} to={to}>
      {body}
    </Link>
  ) : (
    <div className={`tile tile--${tone}`}>{body}</div>
  );
}
